/**
 * Centralized API Fetch Wrapper with Structured Logging
 *
 * Provides structured logging, trace correlation, and timing for all API requests.
 * Use this wrapper instead of raw fetch() for automatic observability.
 */

import { logger } from '@/shared/lib/logger/logger';
import { createTraceParent, extractTraceContextFromResponse } from '@/shared/lib/otel/browser-otel';

export interface ApiFetchOptions extends RequestInit {
  skipLogging?: boolean; // Skip logging for sensitive requests
  logLevel?: 'debug' | 'info' | 'warn'; // Override log level
  context?: Record<string, unknown>; // Additional context for logging
  credentials?: RequestCredentials; // Override credentials mode (default: 'include' for Edge Functions, 'same-origin' for REST API)
  timeout?: number; // Request timeout in milliseconds (default: 30000ms)
}

/**
 * Enhanced fetch wrapper with structured logging and trace correlation
 */
export async function loggedFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
  const {
    skipLogging = false,
    logLevel = 'info',
    context = {},
    timeout = 30000,
    ...fetchOptions
  } = options;

  const method = fetchOptions.method || 'GET';
  const startTime = performance.now();
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Create traceparent header for correlation
  const traceparent = createTraceParent();

  // Prepare headers with trace context
  const headers = new Headers(fetchOptions.headers);
  headers.set('traceparent', traceparent);
  headers.set('x-request-id', requestId);

  // Log request start
  if (!skipLogging) {
    logger.apiCall(method, url, {
      request_id: requestId,
      timeout,
      ...context,
    });
  }

  try {
    // Determine credentials mode:
    // - Default to 'include' for Python FastAPI backend
    // - Use 'same-origin' or explicit override for REST API endpoints (to avoid CORS issues)
    // - Allow explicit override via options.credentials
    const credentialsMode =
      fetchOptions.credentials !== undefined
        ? fetchOptions.credentials
        : url.includes('/rest/v1/')
          ? 'same-origin'
          : 'include';

    // Create AbortController for timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), timeout);

    // Make the request
    const response = await fetch(url, {
      ...fetchOptions,
      method,
      headers,
      credentials: credentialsMode,
      signal: abortController.signal,
    }).finally(() => clearTimeout(timeoutId));

    // Extract trace context from response headers
    extractTraceContextFromResponse(response);

    const duration = Math.round(performance.now() - startTime);
    const backendRequestId =
      response.headers.get('x-request-id') || response.headers.get('cf-ray') || 'unknown';

    // Log response
    if (!skipLogging) {
      if (!response.ok) {
        // Error response
        const errorText = await response
          .clone()
          .text()
          .catch(() => 'Unable to read error body');
        logger.apiError(method, url, new Error(`HTTP ${response.status}: ${errorText}`), {
          request_id: requestId,
          backend_request_id: backendRequestId,
          status: response.status,
          statusText: response.statusText,
          duration,
          ...context,
        });
      } else {
        // Success response
        if (logLevel === 'debug' || logLevel === 'info') {
          logger.info(`API call successful: ${method} ${url}`, {
            action: 'api_call_success',
            method,
            url,
            request_id: requestId,
            backend_request_id: backendRequestId,
            status: response.status,
            duration,
            ...context,
          });
        }
      }
    }

    // Log slow requests as warnings
    if (duration > 5000 && !skipLogging) {
      logger.warn(`Slow API request: ${method} ${url} took ${duration}ms`, {
        action: 'slow_api_request',
        method,
        url,
        duration,
        request_id: requestId,
        ...context,
      });
    }

    return response;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);

    // Log error
    if (!skipLogging) {
      logger.apiError(method, url, error instanceof Error ? error : new Error(String(error)), {
        request_id: requestId,
        duration,
        error_type: error instanceof Error ? error.name : typeof error,
        ...context,
      });
    }

    throw error;
  }
}

/**
 * Convenience wrapper for JSON API calls
 *
 * Note: For FormData requests, don't set Content-Type (browser will set it with boundary)
 */
export async function apiFetchJson<T>(url: string, options: ApiFetchOptions = {}): Promise<T> {
  // Don't set Content-Type for FormData (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  const headers: HeadersInit | undefined = isFormData
    ? options.headers
    : {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined),
      };

  const response = await loggedFetch(url, {
    ...options,
    ...(headers !== undefined ? { headers } : {}),
  });

  if (!response.ok) {
    // Try to parse error response as JSON (backend returns structured errors)
    let errorText: string;
    let errorData: { message?: string; error?: string; detail?: string } | null = null;

    try {
      errorText = await response.text();
      // Try to parse as JSON
      errorData = JSON.parse(errorText) as { message?: string; error?: string; detail?: string };
    } catch {
      // Not JSON, use raw text
      errorText = await response.text().catch(() => 'Unknown error');
    }

    // Use backend's user-friendly message if available
    const message = errorData?.detail || errorData?.message;
    if (message) {
      const error = Object.assign(new Error(message), {
        status: response.status,
        type: errorData?.error,
      });
      throw error;
    }

    throw new Error(`API call failed with status ${response.status}: ${errorText}`);
  }

  return response.json() as Promise<T>;
}

/**
 * Convenience wrapper for POST requests with JSON body
 */
export async function apiPost<T>(
  url: string,
  body: unknown,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {}
): Promise<T> {
  return apiFetchJson<T>(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Convenience wrapper for GET requests
 */
export async function apiGet<T>(
  url: string,
  options: Omit<ApiFetchOptions, 'method'> = {}
): Promise<T> {
  return apiFetchJson<T>(url, {
    ...options,
    method: 'GET',
  });
}
